import { useCallback } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";
import { AccountContext } from "views/AccountWrapper";

import VcsAgentPoolActions from "../components/VcsAgentPoolActions";
import { showVcsAgentPoolDetailsDrawer } from "../components/VcsAgentPoolDetailsDrawer";
import VCSAgentPoolAgentsEmpty from "./Empty";
import { GET_VCS_AGENT_POOL } from "./gql";
import { VCS_AGENT_POOL_AGENTS_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  SORT_CONFIG,
  VCS_AGENT_POOL_AGENTS_TABLE_FILTERS_STORAGE_KEY,
} from "./Table/constants";
import { useSortingVcsAgentConnections } from "./Table/useSortingVcsAgentConnections";
import useVCSAgentPoolAgentsTableStore from "./Table/useTableStore";

const VCSAgentPool = () => {
  const { integrationId: vcsAgentPoolId } = useParams<{ integrationId: string }>();
  const { integration } = useOutletContext<IntegrationsOutletContext>();
  const { accountName } = useTypedContext(AccountContext);

  const navigate = useNavigate();
  const trackAnalytics = useAnalytics();

  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const { data, loading, error } = usePolledQuery(GET_VCS_AGENT_POOL, {
    variables: { vcsAgentPoolId },
    pollingGroup: PollingIntervalGroups.SingleEntities,
  });

  const { filteredAgentConnections } = useSortingVcsAgentConnections(
    data?.vcsAgentPool?.agentConnections
  );

  useTitle(`${data?.vcsAgentPool?.name || "VCS agent pool"} · ${accountName}`);

  const handleDelete = useCallback(() => {
    navigate(`/integrations/${integration.id}`);
  }, [navigate, integration.id]);

  useBreadcrumbs(
    [
      {
        title: "Integrations",
        link: "/integrations",
      },
      {
        title: integration.name,
        link: `/integrations/${integration.id}`,
      },
      {
        title: data?.vcsAgentPool.name ?? "",
      },
    ],
    [data?.vcsAgentPool.name]
  );
  useLayoutHeading(data?.vcsAgentPool.name ?? "");
  useLayoutActions(
    data?.vcsAgentPool ? (
      <>
        <Button
          variant="secondary"
          onPress={() =>
            showVcsAgentPoolDetailsDrawer({
              id: data.vcsAgentPool.id,
              vcsAgentPool: data.vcsAgentPool,
            })
          }
        >
          Details
        </Button>
        <VcsAgentPoolActions
          vcsAgentPool={data?.vcsAgentPool}
          dotsSize="medium"
          onDeleteCallback={handleDelete}
        />
      </>
    ) : null
  );

  const ErrorContent = useErrorHandle(error);
  const loadingContent = loading && !data?.vcsAgentPool;

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loadingContent) {
    return <PageLoading />;
  }

  if (!data?.vcsAgentPool) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageInfoHeader title="View VCS agent pool" />
      <TableSimple
        data={filteredAgentConnections}
        loading={loadingContent}
        store={useVCSAgentPoolAgentsTableStore}
        ariaLabel="VCS agent pool agents list"
        trackAnalytics={trackAnalytics}
        cellPerColumn={VCS_AGENT_POOL_AGENTS_TABLE_CELLS}
        filtersStorageKey={VCS_AGENT_POOL_AGENTS_TABLE_FILTERS_STORAGE_KEY}
        sortConfig={SORT_CONFIG}
        emptyState={<VCSAgentPoolAgentsEmpty />}
      />
    </>
  );
};

export default VCSAgentPool;
